/**
 * Body section of the single-order layout.
 *
 * Owns the regimen info bar plus the RPA-error / order-navigation branch so
 * {@link SingleOrderLayout} stays focused on data wiring rather than markup.
 *
 * @module SingleOrderBody
 */
import React from "react";
import {
  AuthOnFile,
  GeneralOrderDetails,
} from "../../../data-model/medicalPaOrdersModel";
import { useNccnCaseGate } from "../../medicalNecessityAnalysis/hooks/useNccnCaseGate";
import { useNccnReviewProgress } from "../../medicalNecessityAnalysis/hooks/useNccnReviewProgress";
import { useMedicalNecessityButtonShownTracking } from "../hooks/useMedicalNecessityButtonShownTracking";
import { checkRpaError } from "../utils/checkRpaError";
import RpaError from "./errorComponents/rpaError";
import OrderNavigationContent from "./orderNavigationContent";
import RegimenInfoBar from "./regimenInfoBar";

interface ContainerRect {
  left: number;
  top: number;
  width: number;
  right: number;
}

interface SingleOrderBodyProps {
  order: GeneralOrderDetails | null;
  authOnFile: AuthOnFile | null;
  ifMultiplePastRegimenExists: boolean;
  potentialExhaustVisits: boolean;
  containerRef: React.RefObject<HTMLDivElement>;
  containerRect: ContainerRect | null;
  shouldMakeSticky: boolean;
  tabSwitchElements: Array<{ id: string; label: string }>;
  logOnTabClickEvent: (label: string) => void;
  onAuthOnFileRetry: () => void;
  onViewTxPlanClick: () => void;
  /** Forwarded to {@link RegimenInfoBar}; navigates to the medical necessity analysis screen. */
  onMedicalNecessityClick: () => void;
  /**
   * Forwarded to {@link RegimenInfoBar} to open the radiology order PDF
   * in the doc viewer. Only meaningful for radiology orgs.
   */
  onClickViewOncoDocument?: () => void;
  /**
   * Forwarded to {@link RegimenInfoBar} to open the order's clinical
   * document in the doc viewer. Only provided when one is available.
   */
  onClickViewClinicalDocument?: () => void;
}

/**
 * Renders the regimen bar and order navigation (or RPA error) for one order.
 *
 * @param props - Order data, layout metrics, and action handlers
 * @returns The single-order body section
 */
const SingleOrderBody: React.FC<SingleOrderBodyProps> = ({
  order,
  authOnFile,
  ifMultiplePastRegimenExists,
  potentialExhaustVisits,
  containerRef,
  containerRect,
  shouldMakeSticky,
  tabSwitchElements,
  logOnTabClickEvent,
  onAuthOnFileRetry,
  onViewTxPlanClick,
  onMedicalNecessityClick,
  onClickViewOncoDocument,
  onClickViewClinicalDocument,
}) => {
  const {
    isEligible: showMedicalNecessity,
    isResolved: isGateResolved,
    regimenName,
  } = useNccnCaseGate({
    authStatus: authOnFile?.auth_status,
    orderId: order?.order_id ?? undefined,
  });

  useMedicalNecessityButtonShownTracking({
    order,
    isButtonVisible: showMedicalNecessity,
    isGateResolved,
    regimenName,
  });

  const { progress: reviewProgress, isLoading: isReviewProgressLoading } =
    useNccnReviewProgress({
      orderId: showMedicalNecessity
        ? (order?.order_id ?? undefined)
        : undefined,
    });

  return (
    <>
      <div className="shrink-0">
        <RegimenInfoBar
          regimenData={[order?.payload?.regimen_name ?? ""]}
          ifMultiplePastRegimenExists={ifMultiplePastRegimenExists}
          potentialExhaustVisits={potentialExhaustVisits}
          onViewTxPlanClick={onViewTxPlanClick}
          onMedicalNecessityClick={onMedicalNecessityClick}
          onClickViewOncoDocument={onClickViewOncoDocument}
          reviewProgress={reviewProgress}
          isReviewProgressLoading={isReviewProgressLoading}
          showMedicalNecessity={showMedicalNecessity}
          onClickViewClinicalDocument={onClickViewClinicalDocument}
        />
      </div>
      {checkRpaError(authOnFile ?? undefined, order ?? undefined) ? (
        <RpaError onRetry={onAuthOnFileRetry} />
      ) : (
        <OrderNavigationContent
          containerRef={containerRef}
          shouldMakeSticky={shouldMakeSticky}
          containerRect={containerRect}
          tabSwitchElements={tabSwitchElements}
          logOnTabClickEvent={logOnTabClickEvent}
        />
      )}
    </>
  );
};

export default SingleOrderBody;
